<script setup lang="ts">
import { type AnimationPlaybackControlsWithThen, stagger, useAnimate, useInView } from 'motion-v'

const { t, tm } = useI18n()

const items = Object.values(tm('home.partial.aio_marketing.names')).map(
  (name, index) => ({
    name,
    status: Object.values(tm('home.partial.aio_marketing.status'))[index],
    avatar: `${S3_ASSETS_URL}/home/aio-marketing/${index}.jpg`,
    ...getStatusStyling(
      Object.keys(tm('home.partial.aio_marketing.status'))[index] as Parameters<
        typeof getStatusStyling
      >[0],
    ),
  }),
)

const [scope, animate] = useAnimate()
const isInView = useInView(scope)

let sequence: AnimationPlaybackControlsWithThen | undefined

watch(isInView, (inView) => {
  sequence?.stop()

  if (inView) {
    sequence = animate([
      [scope.value, { visiblity: ['hidden', 'visible'] }],
      ['li', { opacity: [0, 1], height: [0, 50], marginTop: [0, '0.5rem'] }, { type: 'spring', stiffness: 80, damping: 12, delay: stagger(1), duration: 0.3 }],
      ['li', { height: 0, marginTop: 0 }, { duration: 0.15, delay: 2 }],
    ], { repeat: Infinity })
  }
})

function getStatusStyling(
  status: 'party' | 'review' | 'catering' | 'reservation',
) {
  return match(status)
    .with(
      'party',
      () =>
        ({
          icon: 'double-tick-outline',
          textColor: 'text-indigo-500',
          backgroundColor: 'bg-indigo-100',
        } as const),
    )
    .with(
      'review',
      () =>
        ({
          icon: 'star-solid',
          textColor: 'text-yellow-600',
          backgroundColor: 'bg-amber-100',
        } as const),
    )
    .with(
      'catering',
      () =>
        ({
          icon: 'dish-outline',
          textColor: 'text-orange-500',
          backgroundColor: 'bg-orange-100',
        } as const),
    )
    .with(
      'reservation',
      () =>
        ({
          icon: 'double-tick-outline',
          textColor: 'text-green-700',
          backgroundColor: 'bg-green-100',
        } as const),
    )
    .exhaustive()
}
</script>

<template>
  <div class="rounded-[1.25rem] border border-black/5 bg-white p-2 shadow-xl md:p-4">
    <div class="flex items-center gap-x-2 text-xl font-semibold">
      <span class="inline-flex rounded-[50%] bg-gray-50 p-2 text-orange-500">
        <UiIcon name="bell-outline" />
      </span>

      <span>{{ t('home.partial.aio_marketing.title') }}</span>
    </div>

    <ul
      ref="scope"
      class="flex flex-col overflow-clip font-medium"
    >
      <li
        v-for="(item, index) in items"
        :key="index"
        :style="{ order: items.length - index }"
        class="w-full overflow-clip rounded-xl border border-gray-100 bg-gray-50"
      >
        <div class="flex items-center justify-between gap-x-3 p-2 font-medium">
          <div class="flex items-center gap-x-2">
            <span
              class="inline-flex size-8 rounded-[50%] border-2 border-white shadow"
            >
              <img
                :src="item.avatar"
                :alt="`${item.name}'s avatar`"
                class="rounded-[inherit]"
              >
            </span>

            <span class="text-sm text-gray-600">
              {{ item.name }}
            </span>
          </div>

          <span
            :class="[
              'inline-flex items-center gap-x-1 rounded-full px-2 py-1 text-xs ring-1 ring-white',
              item.textColor,
              item.backgroundColor,
            ]"
          >
            <UiIcon
              :name="item.icon"
              class="text-sm"
            />

            {{ item.status }}
          </span>
        </div>
      </li>
    </ul>
  </div>
</template>
